// components/ConfettiAnimation.tsx
import { useEffect, useRef } from "react";
import { confetti } from "@tsparticles/confetti";

interface ConfettiAnimationProps {
  containerRef: React.RefObject<HTMLElement | null>;
  visibleFraction?: number; // 0.25 for quarter, 0.5 for half, etc.
}

export const ConfettiAnimation = ({
  containerRef,
  visibleFraction = 0.5, // default to half
}: ConfettiAnimationProps) => {
  const triggered = useRef(false);

  useEffect(() => {
    function checkAndTrigger() {
      if (!containerRef.current || triggered.current) return;
      const rect = containerRef.current.getBoundingClientRect();

      // Calculate how much of the container is visible
      const containerHeight = rect.height;
      const visibleTop = Math.max(rect.top, 0);
      const visibleBottom = Math.min(rect.bottom, window.innerHeight);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      // Trigger if the visible height is at least the desired fraction
      if (visibleHeight >= containerHeight * visibleFraction) {
        triggered.current = true;
        const centerX = rect.left + rect.width / 2;
        confetti({
          particleCount: 300,
          colors: [
            "#FF5257",
            "#61BA47",
            "#007AFF",
            "#F74F9E",
            "#A550A7",
            "#FFC700",
          ],
          shapes: ["triangle", "square"],
          zIndex: 1,
          disableForReducedMotion: true,
          gravity: 1,
          drift: 0,
          origin: { x: centerX / window.innerWidth, y: 0.5 },
          spread: 50,
          startVelocity: 35,
        });
      }
    }
    window.addEventListener("scroll", checkAndTrigger);
    window.addEventListener("resize", checkAndTrigger);
    checkAndTrigger(); // in case already in view
    return () => {
      window.removeEventListener("scroll", checkAndTrigger);
      window.removeEventListener("resize", checkAndTrigger);
    };
  }, [containerRef, visibleFraction]);

  return null;
};
